import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import Modal from '../ui/Modal'
import Textarea from '../ui/Textarea'
import Select from '../ui/Select'
import Button from '../ui/Button'
import SavedIndicator from './SavedIndicator'
import TagInput from './TagInput'
import { classNames } from '../../lib/classNames'
import { formatDateOnly } from '../../lib/noteFilters'

const RichTextEditor = lazy(() => import('./RichTextEditor'))

export default function EditModal({ note, onChange, saving, onClose, onDelete, allTags }) {
    const [local, setLocal] = useState(note)
    const titleRef = useRef(null)

    useEffect(() => {
        titleRef.current?.focus()
    }, [])

    useEffect(() => setLocal(note), [note])

    const handleChange = (field, value) => {
        setLocal((prev) => ({ ...prev, [field]: value }))
        onChange({ [field]: value })
    }

    const isTask = local.kind === 'task'
    const border = isTask && local.done ? 'border-emerald-600' : local.pinned ? 'border-accent' : 'border-black dark:border-white'

    const handleKeyDown = (e) => {
        if (isTask && (e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault()
            handleChange('done', !local.done)
        }
    }

    const header = (
        <div className={classNames('p-4 border-b-2 bg-white dark:bg-zinc-950', border)} onKeyDown={handleKeyDown}>
            <div className="flex items-center gap-2 w-full">
                {isTask && (
                    <input
                        type="checkbox"
                        checked={!!local.done}
                        onChange={(e) => handleChange('done', e.target.checked)}
                        className="size-6 accent-current"
                        title="Mark done"
                        aria-label="Mark task done"
                    />
                )}
                <input
                    ref={titleRef}
                    value={local.title || ''}
                    onChange={(e) => handleChange('title', e.target.value)}
                    placeholder="Title"
                    className="flex-1 bg-transparent text-2xl font-serif font-bold outline-none text-black dark:text-white"
                    aria-label="Title"
                />
                <Button
                    variant={local.pinned ? 'primary' : 'secondary'}
                    className="text-xs px-3 py-1 whitespace-nowrap"
                    onClick={() => handleChange('pinned', !local.pinned)}
                    title={local.pinned ? 'Unpin' : 'Pin'}
                    aria-label={local.pinned ? 'Unpin' : 'Pin'}
                >
                    {local.pinned ? 'Pinned' : 'Pin'}
                </Button>
            </div>

            {isTask ? (
                <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                    <input
                        type="date"
                        value={local.dueAt || ''}
                        onChange={(e) => handleChange('dueAt', e.target.value || null)}
                        className="border-2 border-black dark:border-white bg-white dark:bg-zinc-950 text-black dark:text-white px-3 py-1 text-sm"
                        aria-label="Due date"
                    />
                    <Select value={local.priority || 'medium'} onChange={(e) => handleChange('priority', e.target.value)} className="w-auto" aria-label="Priority">
                        <option value="low">Low priority</option>
                        <option value="medium">Medium priority</option>
                        <option value="high">High priority</option>
                    </Select>
                    {local.dueAt && <span className="text-sm text-zinc-500">Due {formatDateOnly(local.dueAt)}</span>}
                </div>
            ) : (
                <div className="mt-3 flex flex-wrap items-center justify-end gap-3 text-sm">
                    <label className="flex items-center gap-1.5 font-mono text-xs uppercase text-black dark:text-white">
                        <input
                            type="checkbox"
                            checked={!!local.archived}
                            onChange={(e) => handleChange('archived', e.target.checked)}
                            className="h-4 w-4 cursor-pointer accent-current"
                        />
                        Archived
                    </label>
                </div>
            )}

            {!isTask && (
                <div className="mt-3">
                    <TagInput tags={local.tags || []} allTags={allTags} onChange={(tags) => handleChange('tags', tags)} />
                </div>
            )}
        </div>
    )

    const footer = (
        <div className="flex justify-between items-center">
            <Button variant="destructive" onClick={onDelete} aria-label="Delete">
                Delete
            </Button>
            <div className="flex items-center gap-3">
                {saving ? <span className="text-sm text-blue-600">Saving.</span> : <SavedIndicator />}
                <Button onClick={onClose}>Close</Button>
            </div>
        </div>
    )

    return (
        <Modal
            open
            onClose={onClose}
            maxWidth="max-w-4xl"
            ariaLabel={local.title || (isTask ? 'Edit task' : 'Edit note')}
            header={header}
            footer={footer}
        >
            <div className="p-4 min-h-[55vh]" onKeyDown={handleKeyDown}>
                {isTask ? (
                    <Textarea
                        value={local.notes || ''}
                        onChange={(e) => handleChange('notes', e.target.value)}
                        placeholder="Short details for this task..."
                        className="w-full h-40 resize-none"
                        aria-label="Task notes"
                    />
                ) : (
                    <Suspense
                        fallback={
                            <Textarea
                                value={local.content || ''}
                                onChange={(e) => handleChange('content', e.target.value)}
                                placeholder="Loading editor..."
                                className="w-full h-[65vh] resize-none border-none p-0 focus:ring-0 text-lg"
                                aria-label="Content"
                            />
                        }
                    >
                        <RichTextEditor value={local.content || ''} onChange={(md) => handleChange('content', md)} noteId={note._id} />
                    </Suspense>
                )}
            </div>
        </Modal>
    )
}
